import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  Paper,
  Chip,
  Tooltip,
  Divider,
  Stack,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Avatar,
  AvatarGroup,
  Switch,
  InputAdornment,
  Grid } from
'@mui/material';
import {
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Print as PrintIcon,
  QrCode as QrCodeIcon,
  Link as LinkIcon,
  InsertDriveFile as FileIcon,
  Image as ImageIcon,
  PictureAsPdf as PdfIcon,
  Description as WordIcon,
  TableChart as ExcelIcon,
  TextFields as TextIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Group as GroupIcon,
  AttachFile as AttachFileIcon,
  CloudUpload as UploadIcon,
  Download as DownloadIcon,
  Preview as PreviewIcon,
  Notifications as AlarmIcon } from
'@mui/icons-material';
import { QRCodeSVG } from 'qrcode.react';
import { useSnackbar } from 'notistack';
import axios from 'axios';

// Data type definitions
import { useThemeSettings } from "../../contexts/ThemeContext";const DATA_TYPES = {
  TEXT: 'text',
  URL: 'url',
  IMAGE: 'image',
  PDF: 'pdf',
  WORD: 'word',
  EXCEL: 'excel',
  FILE: 'file'
};

// Permission levels
const PERMISSION_LEVELS = {
  PUBLIC: 'public',
  AUTHENTICATED: 'authenticated',
  ADMIN: 'admin'
};

// Helper function to get icon by data type
const getDataTypeIcon = (type) => {
  switch (type) {
    case DATA_TYPES.TEXT:
      return <TextIcon fontSize="small" />;
    case DATA_TYPES.URL:
      return <LinkIcon fontSize="small" />;
    case DATA_TYPES.IMAGE:
      return <ImageIcon fontSize="small" />;
    case DATA_TYPES.PDF:
      return <PdfIcon fontSize="small" />;
    case DATA_TYPES.WORD:
      return <WordIcon fontSize="small" />;
    case DATA_TYPES.EXCEL:
      return <ExcelIcon fontSize="small" />;
    case DATA_TYPES.FILE:
    default:
      return <FileIcon fontSize="small" />;
  }
};

// Helper function to get file extension from filename
const getFileExtension = (filename) => {
  return filename.split('.').pop().toLowerCase();
};

// Helper function to determine data type from file extension
const getDataTypeFromExtension = (extension) => {
  const extensionMap = {
    'jpg': DATA_TYPES.IMAGE,
    'jpeg': DATA_TYPES.IMAGE,
    'png': DATA_TYPES.IMAGE,
    'gif': DATA_TYPES.IMAGE,
    'webp': DATA_TYPES.IMAGE,
    'pdf': DATA_TYPES.PDF,
    'doc': DATA_TYPES.WORD,
    'docx': DATA_TYPES.WORD,
    'xls': DATA_TYPES.EXCEL,
    'xlsx': DATA_TYPES.EXCEL
  };
  return extensionMap[extension] || DATA_TYPES.FILE;
};

const CustomDataPanel = ({
  title = 'Custom Data',
  data = [],
  onSave,
  readOnly = false,
  showPrint = true,
  entityType = 'location', // 'location' or 'device'
  entityId = null,
  availableUsers = [], // List of users for permission assignment
  currentUser = null // Current logged-in user
}) => {const { t } = useThemeSettings();
  const { enqueueSnackbar } = useSnackbar();
  const fileInputRef = useRef(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editableData, setEditableData] = useState([]);
  const [originalData, setOriginalData] = useState([]);
  const [saving, setSaving] = useState(false);
  const [qrCodeDialog, setQrCodeDialog] = useState({ open: false, url: '', name: '' });
  const [previewDialog, setPreviewDialog] = useState({ open: false, item: null });
  const [permissionDialog, setPermissionDialog] = useState({ open: false, item: null });
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    // Initialize data with enhanced properties
    const formattedData = Array.isArray(data) ? data.map((item) => ({
      ...item,
      type: item.type || DATA_TYPES.TEXT,
      visible: item.visible !== undefined ? item.visible : true,
      sendWithAlarm: item.sendWithAlarm || false,
      permissionLevel: item.permissionLevel || PERMISSION_LEVELS.PUBLIC,
      allowedUsers: item.allowedUsers || []
    })) : [];
    setEditableData(formattedData);
    setOriginalData(formattedData);
  }, [data]);

  const handleEdit = () => {
    setIsEditing(true);
    setOriginalData([...editableData]);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditableData([...originalData]);
  };

  const handleSave = async () => {
    // Filter out empty entries
    const cleanedData = editableData.filter((item) => item.name && item.value);

    try {
      setSaving(true);

      if (onSave) {
        await onSave(cleanedData);
      }

      setIsEditing(false);
      setOriginalData(cleanedData);
      setEditableData(cleanedData);

      enqueueSnackbar(`${title} saved successfully!`, {
        variant: 'success',
        autoHideDuration: 3000
      });
    } catch (error) {
      console.error('Error saving custom data:', error);
      enqueueSnackbar(`Failed to save ${title.toLowerCase()}: ${error.message || 'Unknown error'}`, {
        variant: 'error',
        autoHideDuration: 5000
      });
    } finally {
      setSaving(false);
    }
  };

  const handleAddField = () => {
    setEditableData([...editableData, {
      name: '',
      value: '',
      type: DATA_TYPES.TEXT,
      visible: true,
      sendWithAlarm: false,
      permissionLevel: PERMISSION_LEVELS.PUBLIC,
      allowedUsers: []
    }]);
  };

  const handleUpdateField = (index, field, value) => {
    const updated = [...editableData];
    updated[index] = { ...updated[index], [field]: value };
    setEditableData(updated);
  };

  const handleRemoveField = (index) => {
    setEditableData(editableData.filter((_, i) => i !== index));
  };

  // Handle file upload
  const handleFileUpload = async (file, index) => {
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('entityType', entityType);
    formData.append('entityId', entityId);

    try {
      // Upload to files.caro.care
      const response = await axios.post('https://files.caro.care/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      const fileUrl = response.data.url;
      const extension = getFileExtension(file.name);
      const dataType = getDataTypeFromExtension(extension);

      handleUpdateField(index, 'value', fileUrl);
      handleUpdateField(index, 'type', dataType);

      enqueueSnackbar('File uploaded successfully!', { variant: 'success' });
    } catch (error) {
      console.error('Upload error:', error);
      enqueueSnackbar('Failed to upload file', { variant: 'error' });
    } finally {
      setUploading(false);
    }
  };

  // Handle QR code generation
  const handleShowQrCode = (item) => {
    setQrCodeDialog({ open: true, url: item.value, name: item.name });
  };

  // Handle permission management
  const handleOpenPermissionDialog = (item) => {
    setPermissionDialog({ open: true, item });
  };

  const handleSavePermissions = (updatedItem) => {
    const index = editableData.findIndex((item) => item === permissionDialog.item);
    if (index !== -1) {
      handleUpdateField(index, 'permissionLevel', updatedItem.permissionLevel);
      handleUpdateField(index, 'allowedUsers', updatedItem.allowedUsers);
    }
    setPermissionDialog({ open: false, item: null });
  };

  // Check if user has permission to view item
  const hasPermission = (item) => {
    if (!currentUser) return item.permissionLevel === PERMISSION_LEVELS.PUBLIC;

    switch (item.permissionLevel) {
      case PERMISSION_LEVELS.PUBLIC:
        return true;
      case PERMISSION_LEVELS.AUTHENTICATED:
        return !!currentUser;
      case PERMISSION_LEVELS.ADMIN:
        return currentUser?.role === 'admin';
      default:
        return false;
    }
  };

  // Handle file preview
  const handlePreview = (item) => {
    setPreviewDialog({ open: true, item });
  };

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    const printContent = `
      <html>
        <head>
          <title>${title} - ${entityType} ${entityId}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; }
            h2 { color: #333; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #f2f2f2; }
            .metadata { margin-top: 10px; font-size: 12px; color: #666; }
          </style>
        </head>
        <body>
          <h2>${title}</h2>
          <div class="metadata">
            <strong>Entity Type:</strong> ${entityType}<br>
            <strong>Entity ID:</strong> ${entityId || 'N/A'}<br>
            <strong>Printed:</strong> ${new Date().toLocaleString()}
          </div>
          <table>
            <thead>
              <tr>
                <th>Field Name</th>
                <th>Value</th>
              </tr>
            </thead>
            <tbody>
              ${editableData.length > 0 ? editableData.map((item) => `
                <tr>
                  <td>${item.name}</td>
                  <td>${item.value}</td>
                </tr>
              `).join('') : '<tr><td colspan="2" style="text-align: center;">No custom data available</td></tr>'}
            </tbody>
          </table>
        </body>
      </html>
    `;

    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.print();
  };

  return (
    <>
      <Card variant="outlined">
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" component="div">
              {title}
              {editableData.length > 0 &&
              <Chip
                label={editableData.length}
                size="small"
                color="primary"
                sx={{ ml: 1 }} />

              }
            </Typography>

            <Stack direction="row" spacing={1}>
              {showPrint && !isEditing &&
              <Tooltip title={t ? t("CustomDataPanel.print_data") : "Print Data"}>
                  <IconButton size="small" onClick={handlePrint}>
                    <PrintIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              }

              {!readOnly && !isEditing &&
              <Tooltip title={t ? t("CustomDataPanel.edit_data") : "Edit Data"}>
                  <IconButton size="small" onClick={handleEdit} color="primary">
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              }

              {isEditing &&
              <>
                  <Tooltip title={t ? t("CustomDataPanel.save_changes") : "Save Changes"}>
                    <IconButton
                    size="small"
                    onClick={handleSave}
                    color="success"
                    disabled={saving}>
                    
                      {saving ? <CircularProgress size={18} /> : <SaveIcon fontSize="small" />}
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={t ? t("CustomDataPanel.cancel") : "Cancel"}>
                    <IconButton
                    size="small"
                    onClick={handleCancel}
                    color="error"
                    disabled={saving}>
                    
                      <CancelIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </>
              }
            </Stack>
          </Box>

          <Divider sx={{ mb: 2 }} />

          {isEditing ?
          <Box>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell width={40}>
                        <Tooltip title={t ? t("CustomDataPanel.select_type_data_text_url_can_be") : "Select the type of data: Text, URL (can be displayed as QR code), Image, PDF, Word document, Excel spreadsheet, or generic file"}>
                          <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'help' }}>{t ? t("CustomDataPanel.type") : "Type"}

                        </Box>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Tooltip title={t ? t("CustomDataPanel.enter_descriptive_name_this_field") : "Enter a descriptive name for this field (e.g., 'Emergency Contact', 'Manual PDF', 'Location Map')"}>
                          <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'help' }}>{t ? t("CustomDataPanel.field_name") : "Field Name"}

                        </Box>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Tooltip title={t ? t("CustomDataPanel.enter_value_upload_file_urls_ca") : "Enter the value or upload a file. URLs can be displayed as QR codes, files will be stored on files.caro.care"}>
                          <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'help' }}>
                            Value
                          </Box>
                        </Tooltip>
                      </TableCell>
                      <TableCell width={100}>
                        <Tooltip title={t ? t("CustomDataPanel.toggle_whether_this_field_is_visible") : "Toggle whether this field is visible in the normal view. Hidden fields can still be accessed when editing"}>
                          <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'help' }}>{t ? t("CustomDataPanel.visibility") : "Visibility"}

                        </Box>
                        </Tooltip>
                      </TableCell>
                      <TableCell width={100}>
                        <Tooltip title={t ? t("CustomDataPanel.check_include_this_data_when_sending") : "Check to include this data when sending alarm notifications. Important information like emergency procedures should have this enabled"}>
                          <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'help' }}>{t ? t("CustomDataPanel.alarm") : "Alarm"}

                        </Box>
                        </Tooltip>
                      </TableCell>
                      <TableCell width={100}>
                        <Tooltip title={t ? t("CustomDataPanel.control_who_can_see_this_data_public_eve") : "Control who can see this data: Public (everyone), Users (authenticated users only), or Admin (administrators only)"}>
                          <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'help' }}>{t ? t("CustomDataPanel.access") : "Access"}

                        </Box>
                        </Tooltip>
                      </TableCell>
                      <TableCell width={80} align="center">{t ? t("CustomDataPanel.actions") : "Actions"}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {editableData.map((item, index) =>
                  <TableRow key={index}>
                        <TableCell>
                          <Tooltip title={t ? t("CustomDataPanel.choose_appropriate_data_ty") : "Choose the appropriate data type for this field"}>
                            <FormControl size="small" fullWidth>
                              <Select
                            value={item.type}
                            onChange={(e) => handleUpdateField(index, 'type', e.target.value)}
                            variant="standard">
                            
                                <MenuItem value={DATA_TYPES.TEXT}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <TextIcon fontSize="small" />
                                    <Typography variant="caption">{t ? t("CustomDataPanel.text") : "Text"}</Typography>
                                  </Box>
                                </MenuItem>
                                <MenuItem value={DATA_TYPES.URL}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <LinkIcon fontSize="small" />
                                    <Typography variant="caption">URL</Typography>
                                  </Box>
                                </MenuItem>
                                <MenuItem value={DATA_TYPES.IMAGE}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <ImageIcon fontSize="small" />
                                    <Typography variant="caption">{t ? t("CustomDataPanel.image") : "Image"}</Typography>
                                  </Box>
                                </MenuItem>
                                <MenuItem value={DATA_TYPES.PDF}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <PdfIcon fontSize="small" />
                                    <Typography variant="caption">PDF</Typography>
                                  </Box>
                                </MenuItem>
                                <MenuItem value={DATA_TYPES.WORD}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <WordIcon fontSize="small" />
                                    <Typography variant="caption">{t ? t("CustomDataPanel.word") : "Word"}</Typography>
                                  </Box>
                                </MenuItem>
                                <MenuItem value={DATA_TYPES.EXCEL}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <ExcelIcon fontSize="small" />
                                    <Typography variant="caption">{t ? t("CustomDataPanel.excel") : "Excel"}</Typography>
                                  </Box>
                                </MenuItem>
                                <MenuItem value={DATA_TYPES.FILE}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <FileIcon fontSize="small" />
                                    <Typography variant="caption">{t ? t("CustomDataPanel.file") : "File"}</Typography>
                                  </Box>
                                </MenuItem>
                              </Select>
                            </FormControl>
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          <TextField
                        fullWidth
                        size="small"
                        variant="standard"
                        value={item.name}
                        onChange={(e) => handleUpdateField(index, 'name', e.target.value)}
                        placeholder={t ? t("CustomDataPanel.field_name") : "Field name"} />
                      
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {item.type === DATA_TYPES.IMAGE ||
                        item.type === DATA_TYPES.PDF ||
                        item.type === DATA_TYPES.WORD ||
                        item.type === DATA_TYPES.EXCEL ||
                        item.type === DATA_TYPES.FILE ?
                        <>
                                <input
                            ref={fileInputRef}
                            type="file"
                            style={{ display: 'none' }}
                            onChange={(e) => handleFileUpload(e.target.files[0], index)}
                            accept={
                            item.type === DATA_TYPES.IMAGE ? 'image/*' :
                            item.type === DATA_TYPES.PDF ? '.pdf' :
                            item.type === DATA_TYPES.WORD ? '.doc,.docx' :
                            item.type === DATA_TYPES.EXCEL ? '.xls,.xlsx' :
                            '*'
                            } />
                          
                                <TextField
                            size="small"
                            variant="standard"
                            value={item.value}
                            onChange={(e) => handleUpdateField(index, 'value', e.target.value)}
                            placeholder={t ? t("CustomDataPanel.file_url") : "File URL"}
                            sx={{ flex: 1 }} />
                          
                                <Tooltip title={uploading ? "Uploading file..." : "Click to upload a file to files.caro.care"}>
                                  <IconButton
                              size="small"
                              onClick={() => fileInputRef.current?.click()}
                              disabled={uploading}>
                              
                                    {uploading ? <CircularProgress size={16} /> : <UploadIcon fontSize="small" />}
                                  </IconButton>
                                </Tooltip>
                              </> :

                        <TextField
                          fullWidth
                          size="small"
                          variant="standard"
                          value={item.value}
                          onChange={(e) => handleUpdateField(index, 'value', e.target.value)}
                          placeholder={item.type === DATA_TYPES.URL ? "Enter URL" : "Enter value"} />

                        }
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Tooltip title={item.visible ? "Field is visible to users" : "Field is hidden from normal view"}>
                            <Switch
                          size="small"
                          checked={item.visible}
                          onChange={(e) => handleUpdateField(index, 'visible', e.target.checked)} />
                        
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          <Tooltip title={item.sendWithAlarm ? "This data will be included in alarm notifications" : "This data will NOT be sent with alarms"}>
                            <Checkbox
                          size="small"
                          checked={item.sendWithAlarm}
                          onChange={(e) => handleUpdateField(index, 'sendWithAlarm', e.target.checked)}
                          icon={<AlarmIcon fontSize="small" />}
                          checkedIcon={<AlarmIcon fontSize="small" color="primary" />} />
                        
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          <Tooltip title={t ? t("CustomDataPanel.select_who_can_view_this_data") : "Select who can view this data"}>
                            <FormControl size="small" fullWidth>
                              <Select
                            value={item.permissionLevel}
                            onChange={(e) => handleUpdateField(index, 'permissionLevel', e.target.value)}
                            variant="standard">
                            
                                <MenuItem value={PERMISSION_LEVELS.PUBLIC}>
                                  <Tooltip title={t ? t("CustomDataPanel.anyone_can_view_this_data") : "Anyone can view this data"} placement="right">
                                    <Box>{t ? t("CustomDataPanel.public") : "Public"}</Box>
                                  </Tooltip>
                                </MenuItem>
                                <MenuItem value={PERMISSION_LEVELS.AUTHENTICATED}>
                                  <Tooltip title={t ? t("CustomDataPanel.only_loggedin_users_can_view_t") : "Only logged-in users can view this data"} placement="right">
                                    <Box>{t ? t("CustomDataPanel.users") : "Users"}</Box>
                                  </Tooltip>
                                </MenuItem>
                                <MenuItem value={PERMISSION_LEVELS.ADMIN}>
                                  <Tooltip title={t ? t("CustomDataPanel.only_administrators_can_view_t") : "Only administrators can view this data"} placement="right">
                                    <Box>{t ? t("CustomDataPanel.admin") : "Admin"}</Box>
                                  </Tooltip>
                                </MenuItem>
                              </Select>
                            </FormControl>
                          </Tooltip>
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title={t ? t("CustomDataPanel.remove_this_field") : "Remove this field"}>
                            <IconButton
                          size="small"
                          onClick={() => handleRemoveField(index)}
                          color="error">
                          
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                  )}
                    {editableData.length === 0 &&
                  <TableRow>
                        <TableCell colSpan={7} align="center">
                          <Typography variant="body2" color="text.secondary">{t ? t("CustomDataPanel.no_custom_data_fields_click_add_field") : "No custom data fields. Click \"Add Field\" to create one."}

                      </Typography>
                        </TableCell>
                      </TableRow>
                  }
                  </TableBody>
                </Table>
              </TableContainer>

              <Button
              startIcon={<AddIcon />}
              onClick={handleAddField}
              sx={{ mt: 2 }}
              variant="outlined"
              size="small">{t ? t("CustomDataPanel.add_field") : "Add Field"}


            </Button>
            </Box> :

          <Box>
              {editableData.length > 0 ?
            <Grid container spacing={2}>
                  {editableData.filter((item) => item.visible && hasPermission(item)).map((item, index) =>
              <Grid item xs={12} key={index}>
                      <Paper variant="outlined" sx={{ p: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {getDataTypeIcon(item.type)}
                            <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
                              {item.name}
                            </Typography>
                            {item.sendWithAlarm &&
                      <Tooltip title={t ? t("CustomDataPanel.this_data_will_be_included_whe") : "This data will be included when sending alarm notifications"}>
                                <Chip
                          icon={<AlarmIcon />}
                          label={t ? t("CustomDataPanel.sends_alarm") : "Sends with Alarm"}
                          size="small"
                          color="warning"
                          variant="outlined" />
                        
                              </Tooltip>
                      }
                            {item.permissionLevel !== PERMISSION_LEVELS.PUBLIC &&
                      <Tooltip title={
                      item.permissionLevel === PERMISSION_LEVELS.ADMIN ?
                      'Only administrators can view this data' :
                      'Only authenticated users can view this data'
                      }>
                                <Chip
                          icon={<GroupIcon />}
                          label={
                          item.permissionLevel === PERMISSION_LEVELS.ADMIN ? 'Admin Only' :
                          item.permissionLevel === PERMISSION_LEVELS.AUTHENTICATED ? 'Users Only' :
                          'Restricted'
                          }
                          size="small"
                          color="info"
                          variant="outlined" />
                        
                              </Tooltip>
                      }
                          </Box>
                          <Stack direction="row" spacing={1}>
                            {item.type === DATA_TYPES.URL &&
                      <Tooltip title={t ? t("CustomDataPanel.show_qr_code_this_url") : "Show QR code for this URL"}>
                                <IconButton size="small" onClick={() => handleShowQrCode(item)}>
                                  <QrCodeIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                      }
                            {(item.type === DATA_TYPES.IMAGE ||
                      item.type === DATA_TYPES.PDF) &&
                      <Tooltip title={t ? t("CustomDataPanel.preview_this_file") : "Preview this file"}>
                                <IconButton size="small" onClick={() => handlePreview(item)}>
                                  <PreviewIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                      }
                            {(item.type === DATA_TYPES.FILE ||
                      item.type === DATA_TYPES.WORD ||
                      item.type === DATA_TYPES.EXCEL ||
                      item.type === DATA_TYPES.PDF) &&
                      <Tooltip title={t ? t("CustomDataPanel.download_this_file") : "Download this file"}>
                                <IconButton
                          size="small"
                          component="a"
                          href={item.value}
                          target="_blank"
                          rel="noopener noreferrer">
                          
                                  <DownloadIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                      }
                          </Stack>
                        </Box>
                        <Box sx={{ mt: 1 }}>
                          {item.type === DATA_TYPES.URL ?
                    <a
                      href={item.value}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: 'inherit', textDecoration: 'none' }}>
                      
                              <Typography
                        variant="body2"
                        sx={{
                          color: 'primary.main',
                          textDecoration: 'underline',
                          '&:hover': { opacity: 0.8 }
                        }}>
                        
                                {item.value}
                              </Typography>
                            </a> :
                    item.type === DATA_TYPES.IMAGE ?
                    <Box
                      sx={{
                        maxWidth: 200,
                        maxHeight: 150,
                        overflow: 'hidden',
                        borderRadius: 1,
                        mt: 1
                      }}>
                      
                              <img
                        src={item.value}
                        alt={item.name}
                        style={{ width: '100%', height: 'auto' }} />
                      
                            </Box> :

                    <Typography variant="body2" color="text.secondary">
                              {item.value}
                            </Typography>
                    }
                        </Box>
                      </Paper>
                    </Grid>
              )}
                  {editableData.filter((item) => item.visible && hasPermission(item)).length === 0 &&
              <Grid item xs={12}>
                      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>{t ? t("CustomDataPanel.no_custom_data_available_you_dont_hav") : "No custom data available or you don't have permission to view it"}

                </Typography>
                    </Grid>
              }
                </Grid> :

            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>{t ? t("CustomDataPanel.no_custom_data_available") : "No custom data available"}

            </Typography>
            }
            </Box>
          }
        </CardContent>
      </Card>

      {/* QR Code Dialog */}
      <Dialog
        open={qrCodeDialog.open}
        onClose={() => setQrCodeDialog({ open: false, url: '', name: '' })}
        maxWidth="xs"
        fullWidth>
        
        <DialogTitle>{t ? t("CustomDataPanel.qr_code") : "QR Code -"}{qrCodeDialog.name}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <QRCodeSVG value={qrCodeDialog.url} size={200} />
          </Box>
          <Typography variant="body2" sx={{ mt: 2, textAlign: 'center', wordBreak: 'break-all' }}>
            {qrCodeDialog.url}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setQrCodeDialog({ open: false, url: '', name: '' })}>{t ? t("CustomDataPanel.close") : "Close"}

          </Button>
        </DialogActions>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog
        open={previewDialog.open}
        onClose={() => setPreviewDialog({ open: false, item: null })}
        maxWidth="md"
        fullWidth>
        
        <DialogTitle>{previewDialog.item?.name}</DialogTitle>
        <DialogContent>
          {previewDialog.item?.type === DATA_TYPES.IMAGE ?
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <img
              src={previewDialog.item.value}
              alt={previewDialog.item.name}
              style={{ maxWidth: '100%', height: 'auto' }} />
            
            </Box> :
          previewDialog.item?.type === DATA_TYPES.PDF ?
          <iframe
            src={previewDialog.item.value}
            width="100%"
            height="500px"
            title={previewDialog.item.name}
            style={{ border: 'none' }} /> :

          null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewDialog({ open: false, item: null })}>{t ? t("CustomDataPanel.close") : "Close"}

          </Button>
        </DialogActions>
      </Dialog>
    </>);

};

export default CustomDataPanel;